import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { queryService } from '../services/queryService';
import { conversationService } from '../services/conversationService';
import { ConversationItem, QueryResult, FeedbackType, StoredMessage } from '../types';
import ChartComponent, { ChartComponentHandle } from '../components/ChartComponent';
import { ConversationSidebar } from '../components/ConversationSidebar';
import './QueryPage.css';

function storedMessagesToItems(messages: StoredMessage[]): ConversationItem[] {
  return messages.map((m) => ({
    question: m.question,
    answer: m.answer,
    daxQuery: m.daxQuery,
    chartData: m.chartData,
    timestamp: new Date(m.timestamp),
    feedback: m.feedback ?? undefined,
    feedbackComment: m.feedbackComment,
    id: m.id,
  }));
}

const QueryPage: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const [visibleCharts, setVisibleCharts] = useState<Set<number>>(new Set());
  const [editedChartTypes, setEditedChartTypes] = useState<Map<number, 'bar' | 'line' | 'pie' | 'doughnut'>>(new Map());
  const [negativeFeedbackIndex, setNegativeFeedbackIndex] = useState<number | null>(null);
  const [feedbackCommentDraft, setFeedbackCommentDraft] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [conversationList, setConversationList] = useState<{ id: string; title: string; updatedAt: string }[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const chartRefs = useRef<Map<number, React.RefObject<ChartComponentHandle | null>>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const refreshConversationList = useCallback(async () => {
    try {
      const list = await conversationService.listConversations();
      setConversationList(list);
    } catch (e) {
      console.error('Failed to refresh conversation list', e);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSidebarLoading(true);
      try {
        const list = await conversationService.listConversations();
        if (cancelled) return;
        setConversationList(list);
        if (list.length === 0) {
          const newConv = await conversationService.createConversation();
          if (cancelled) return;
          setActiveConversationId(newConv.id);
          setConversation([]);
          setConversationList((prev) => [...prev, { id: newConv.id, title: newConv.title, updatedAt: newConv.updatedAt }]);
        }
      } catch (e) {
        console.error('Failed to load conversations', e);
      } finally {
        if (!cancelled) setSidebarLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelectConversation = useCallback(async (id: string) => {
    if (id === activeConversationId) return;
    setActiveConversationId(id);
    try {
      const conv = await conversationService.getConversation(id);
      if (conv) setConversation(storedMessagesToItems(conv.messages));
      else setConversation([]);
    } catch (e) {
      console.error('Failed to load conversation', e);
      setConversation([]);
    }
  }, [activeConversationId]);

  const handleNewChat = useCallback(async () => {
    try {
      const newConv = await conversationService.createConversation();
      setActiveConversationId(newConv.id);
      setConversation([]);
      setVisibleCharts(new Set());
      setEditedChartTypes(new Map());
      chartRefs.current.clear();
      setConversationList((prev) => [{ id: newConv.id, title: newConv.title, updatedAt: newConv.updatedAt }, ...prev]);
    } catch (e) {
      console.error('Failed to create new conversation', e);
    }
  }, []);

  const handleRenameConversation = useCallback(async (id: string, title: string) => {
    await conversationService.renameConversation(id, title);
    await refreshConversationList();
  }, [refreshConversationList]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    await conversationService.deleteConversation(id);
    if (activeConversationId === id) {
      const remaining = conversationList.filter((c) => c.id !== id);
      if (remaining.length > 0) {
        handleSelectConversation(remaining[0].id);
      } else {
        const newConv = await conversationService.createConversation();
        setActiveConversationId(newConv.id);
        setConversation([]);
        setConversationList([{ id: newConv.id, title: newConv.title, updatedAt: newConv.updatedAt }]);
      }
    }
    await refreshConversationList();
  }, [activeConversationId, conversationList, handleSelectConversation, refreshConversationList]);

  // Create refs for each conversation item with chart data
  useEffect(() => {
    conversation.forEach((item, idx) => {
      if (item.chartData && !chartRefs.current.has(idx)) {
        chartRefs.current.set(idx, React.createRef<ChartComponentHandle | null>());
      }
    });
  }, [conversation]);

  // Auto-scroll to bottom when conversation updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!question.trim() || loading) return;

    setLoading(true);
    const currentQuestion = question;
    setQuestion('');

    // Immediately add user's question with loading state
    const pendingItem: ConversationItem = {
      question: currentQuestion,
      timestamp: new Date(),
      loading: true,
    };
    const updatedConversation = [...conversation, pendingItem];
    setConversation(updatedConversation);

    try {
      const result: QueryResult = await queryService.askQuestion(
        currentQuestion,
        conversation,
        activeConversationId ?? undefined
      );

      if (result.conversationId && !activeConversationId) {
        setActiveConversationId(result.conversationId);
        refreshConversationList();
      } else if (result.conversationId) {
        refreshConversationList();
      }

      const completedItem: ConversationItem = {
        question: currentQuestion,
        answer: result.answer,
        daxQuery: result.daxQuery,
        chartData: result.chartData,
        timestamp: new Date(),
        loading: false,
        id: result.messageId,
      };

      const finalConversation = [...conversation, completedItem];
      setConversation(finalConversation);
    } catch (error) {
      console.error('Error asking question:', error);
      
      // Update the pending item with error message
      const errorItem: ConversationItem = {
        question: currentQuestion,
        answer: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date(),
        loading: false,
      };
      
      // Replace the last item (pending) with error item
      const finalConversation = [...conversation, errorItem];
      setConversation(finalConversation);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    handleNewChat();
  };

  const toggleChart = (index: number) => {
    const newVisible = new Set(visibleCharts);
    if (newVisible.has(index)) {
      newVisible.delete(index);
    } else {
      newVisible.add(index);
    }
    setVisibleCharts(newVisible);
  };

  const exportToCSV = (item: ConversationItem, index: number) => {
    if (!item.chartData) return;

    const { labels, datasets } = item.chartData;
    
    // Build CSV content
    let csvContent = 'Label';
    datasets.forEach(dataset => {
      csvContent += ',' + (dataset.label || 'Value');
    });
    csvContent += '\n';

    // Add data rows
    labels.forEach((label, idx) => {
      csvContent += `"${label}"`;
      datasets.forEach(dataset => {
        const value = Array.isArray(dataset.data) ? dataset.data[idx] : dataset.data;
        csvContent += ',' + (value || 0);
      });
      csvContent += '\n';
    });

    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `powerbi-data-${timestamp}-${index + 1}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportChartAsImage = (index: number) => {
    const chartRef = chartRefs.current.get(index);
    if (chartRef?.current) {
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `powerbi-chart-${timestamp}-${index + 1}.png`;
      chartRef.current.exportAsImage(filename);
    }
  };

  const handleChartTypeChange = (index: number, newType: 'bar' | 'line' | 'pie' | 'doughnut') => {
    const newEditedChartTypes = new Map(editedChartTypes);
    newEditedChartTypes.set(index, newType);
    setEditedChartTypes(newEditedChartTypes);
  };

  const handleFeedbackClick = async (index: number, type: FeedbackType) => {
    const item = conversation[index];
    if (!item || item.feedback || item.loading) return;

    if (type === 'positive') {
      setFeedbackSubmitting(true);
      await queryService.submitFeedback(item.question, item.answer, 'positive', {
        conversationIndex: index,
        conversationId: activeConversationId ?? undefined,
        messageId: item.id,
      });
      setFeedbackSubmitting(false);
      const next = [...conversation];
      next[index] = { ...item, feedback: 'positive' };
      setConversation(next);
      return;
    }

    setNegativeFeedbackIndex(index);
    setFeedbackCommentDraft('');
  };

  const handleNegativeFeedbackSubmit = async (index: number) => {
    const item = conversation[index];
    if (!item) return;

    setFeedbackSubmitting(true);
    await queryService.submitFeedback(item.question, item.answer, 'negative', {
      comment: feedbackCommentDraft.trim() || undefined,
      conversationIndex: index,
      conversationId: activeConversationId ?? undefined,
      messageId: item.id,
    });
    setFeedbackSubmitting(false);
    const next = [...conversation];
    next[index] = {
      ...item,
      feedback: 'negative',
      feedbackComment: feedbackCommentDraft.trim() || undefined,
    };
    setConversation(next);
    setNegativeFeedbackIndex(null);
    setFeedbackCommentDraft('');
  };

  const getChartData = (item: ConversationItem, index: number) => {
    if (!item.chartData) return null;
    
    const editedType = editedChartTypes.get(index);
    if (editedType && editedType !== item.chartData.chartType) {
      return {
        ...item.chartData,
        chartType: editedType,
      };
    }
    return item.chartData;
  };

  const exampleQuestions = [
    "Who were the top 10 TAMs by revenue last month?",
    "What were the top performing practices for the top 3 performing TAMs?",
    "Which region had the highest revenue last month?",

  ];

  const handleExampleClick = (example: string) => {
    setQuestion(example);
  };

  return (
    <div className="query-page-layout">
      <ConversationSidebar
        conversations={conversationList}
        activeId={activeConversationId}
        loading={sidebarLoading}
        onSelect={handleSelectConversation}
        onNewChat={handleNewChat}
        onRename={handleRenameConversation}
        onDelete={handleDeleteConversation}
      />
      <div className="query-page-main">
      <h1 className="query-title neuro-styrene-bold">Ask Questions</h1>
      <p className="query-subtitle neuro-silkafont-regular">
        Ask questions about Newton data in plain English
      </p>
      <div className="query-page">
      {/* Conversation History */}
      <div className="query-conversation">
        {conversation.length === 0 ? (
          <div className="query-empty">
            <div className="query-empty-icon">💭</div>
            <h2 className="query-empty-title neuro-styrene-bold">
              Start a conversation
            </h2>
            <p className="query-empty-text neuro-silkafont-regular">
              Try asking one of these example questions:
            </p>
            <div className="query-examples">
              {exampleQuestions.map((example, idx) => (
                <button
                  key={idx}
                  className="query-example-btn neuro-silkafont-regular"
                  onClick={() => handleExampleClick(example)}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="query-messages">
              {conversation.map((item, idx) => (
                <div key={idx} className="query-message-group">
                  {/* User Question */}
                  <div className="query-message query-message-user">
                    <div className="query-message-content">
                      <div className="query-message-text neuro-silkafont-regular">
                        {item.question}
                      </div>
                    </div>
                  </div>

                  {/* AI Answer */}
                  <div className="query-message query-message-assistant">
                    <div className="query-message-content">
                      {item.loading ? (
                        <div className="query-message-loading neuro-silkafont-regular">
                          <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                          <span className="loading-text">Thinking...</span>
                        </div>
                      ) : (
                        <>
                          <div className="query-message-text neuro-silkafont-regular">
                            <ReactMarkdown>{item.answer}</ReactMarkdown>
                          </div>
                          
                          {/* Chart and Export Controls */}
                          {item.chartData && (
                        <div className="query-chart-section">
                          <div className="query-action-buttons">
                            <button
                              onClick={() => toggleChart(idx)}
                              className="query-chart-toggle neuro-silkafont-regular"
                            >
                              {visibleCharts.has(idx) ? '📊 Hide Chart' : '📊 Show Chart'}
                            </button>
                            
                            {visibleCharts.has(idx) && (
                              <div className="query-chart-type-selector">
                                <label className="query-chart-type-label neuro-silkafont-regular">
                                  Chart Type:
                                </label>
                                <select
                                  value={editedChartTypes.get(idx) || item.chartData.chartType}
                                  onChange={(e) => handleChartTypeChange(idx, e.target.value as 'bar' | 'line' | 'pie' | 'doughnut')}
                                  className="query-chart-type-dropdown neuro-silkafont-regular"
                                >
                                  <option value="bar">📊 Bar Chart</option>
                                  <option value="line">📈 Line Chart</option>
                                  <option value="pie">🥧 Pie Chart</option>
                                  <option value="doughnut">🍩 Doughnut Chart</option>
                                </select>
                              </div>
                            )}
                            
                            <button
                              onClick={() => exportChartAsImage(idx)}
                              className="query-export-btn neuro-silkafont-regular"
                            >
                              📸 Export Chart
                            </button>
                            
                            <button
                              onClick={() => exportToCSV(item, idx)}
                              className="query-export-btn query-export-csv neuro-silkafont-regular"
                            >
                              📥 Export Data
                            </button>
                          </div>
                          
                          {/* Chart Visualization */}
                          {visibleCharts.has(idx) && (
                            <div className="query-chart-container">
                              <ChartComponent 
                                ref={chartRefs.current.get(idx)!}
                                data={getChartData(item, idx)!} 
                              />
                            </div>
                          )}
                        </div>
                      )}

                          {/* DAX Query (Expandable) */}
                          {item.daxQuery && (
                            <details className="query-dax-details">
                              <summary className="query-dax-summary neuro-silkafont-regular">
                                View DAX Query
                              </summary>
                              <pre className="query-dax-code">{item.daxQuery}</pre>
                            </details>
                          )}

                          {/* Response feedback */}
                          <div className="query-feedback neuro-silkafont-regular">
                            {item.feedback ? (
                              <span className="query-feedback-thanks">
                                Thanks for your feedback!
                              </span>
                            ) : negativeFeedbackIndex === idx ? (
                              <div className="query-feedback-negative-form">
                                <label className="query-feedback-comment-label">
                                  Add a comment (optional)
                                </label>
                                <textarea
                                  value={feedbackCommentDraft}
                                  onChange={(e) => setFeedbackCommentDraft(e.target.value)}
                                  placeholder="What could we improve?"
                                  className="query-feedback-comment-input"
                                  rows={2}
                                  disabled={feedbackSubmitting}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleNegativeFeedbackSubmit(idx)}
                                  className="query-feedback-submit-btn"
                                  disabled={feedbackSubmitting}
                                >
                                  {feedbackSubmitting ? 'Sending...' : 'Send feedback'}
                                </button>
                              </div>
                            ) : (
                              <>
                                <span className="query-feedback-prompt">Was this helpful?</span>
                                <div className="query-feedback-buttons">
                                  <button
                                    type="button"
                                    onClick={() => handleFeedbackClick(idx, 'positive')}
                                    className="query-feedback-btn query-feedback-btn-up"
                                    disabled={feedbackSubmitting}
                                    aria-label="Yes, helpful"
                                  >
                                    👍
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleFeedbackClick(idx, 'negative')}
                                    className="query-feedback-btn query-feedback-btn-down"
                                    disabled={feedbackSubmitting}
                                    aria-label="No, not helpful"
                                  >
                                    👎
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            <button onClick={handleClear} className="btn-secondary query-clear-btn">
              Clear Conversation
            </button>
          </>
        )}
      </div>

      {/* Input Form */}
      <div className="query-input-container">
        <form onSubmit={handleSubmit} className="query-form">
          <div className="query-input-wrapper">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask a question about your data..."
              className="query-input neuro-silkafont-regular"
              disabled={loading}
            />
            <button
              type="submit"
              className="btn-primary query-submit-btn"
              disabled={loading || !question.trim()}
            >
              {loading ? 'Thinking...' : 'Ask'}
            </button>
          </div>
        </form>
      </div>
      </div>
      </div>
    </div>
  );
};

export default QueryPage;
